import { useEffect, useState, useCallback } from "react";
import { Download, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { Button } from "../../../../components/ui/button";
import { Badge } from "../../../../components/ui/badge";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import {
  SessionDocument,
  GetDocumentationResponseSchema,
  DownloadDocumentResponseSchema,
} from "../../../../models/audit/sessionSupplementaryDocument";
import { fetchFastApi } from "../../../../api/fastapi";
import { matchingSessionApiBase } from "../../../../lib/sessionApiBase";
import type { SessionKind } from "../../../../hooks/audit/useControlSessionEnvelope";
import { toast } from "sonner";

interface SupplementaryDocumentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The control session or work step session whose documents to list. */
  sessionId: string;
  sessionKind: SessionKind;
  onDocumentDeleted?: () => void;
}

export const SupplementaryDocumentsModal = ({
  open,
  onOpenChange,
  sessionId,
  sessionKind,
  onDocumentDeleted,
}: SupplementaryDocumentsModalProps) => {
  const [documents, setDocuments] = useState<SessionDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(
    null,
  );
  const [confirmDeleteDoc, setConfirmDeleteDoc] =
    useState<SessionDocument | null>(null);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    string | null
  >(null);

  const sessionBase = matchingSessionApiBase(sessionKind, sessionId);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        method: "GET",
        path: `${sessionBase}/documentation`,
      });

      const parsed = GetDocumentationResponseSchema.parse(response);

      // Transform backend documents to SessionDocuments with derived documentSource
      const transformedDocuments: SessionDocument[] = parsed.documents.map(
        (doc) => {
          // Derive documentSource from which parent ID field is present. A
          // supplementary document names its session (control or work step,
          // never both — main_0034's parent check); an entity document names
          // the control or the work step it belongs to.
          let documentSource: SessionDocument["documentSource"];
          if (doc.controlSessionId || doc.workStepSessionId) {
            documentSource = "session_supplementary";
          } else if (doc.workStepId) {
            documentSource = "work_step";
          } else if (doc.controlId) {
            // TODO: Determine if it's current_control or parent_control
            // For now, default to current_control
            documentSource = "current_control";
          } else {
            // Fallback - shouldn't happen
            documentSource = "current_control";
          }

          return {
            id: doc.id,
            documentId: doc.documentId,
            documentSource,
            docType: doc.docType,
            sourceSystem: doc.sourceSystem,
            documentType: doc.documentType ?? doc.fullDocument.documentType,
            state: doc.state ?? doc.fullDocument.state,
            fileName: doc.fullDocument.fileName,
            fileType: doc.fullDocument.fileType,
            createdAt: doc.createdAt,
            updatedAt: doc.updatedAt,
            fullDocument: doc.fullDocument,
          };
        },
      );

      setDocuments(transformedDocuments);
    } catch (error) {
      console.error("Error loading documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [sessionBase]);

  useEffect(() => {
    if (open) {
      loadDocuments();
    }
  }, [open, loadDocuments]);

  const handleDownload = async (doc: SessionDocument) => {
    setDownloadingDocumentId(doc.id);
    try {
      const response = await fetchFastApi({
        method: "GET",
        path: `${sessionBase}/supplementary-document/${doc.id}/download`,
      });

      const parsed = DownloadDocumentResponseSchema.parse(response);

      // Open the presigned URL in a new tab to download
      window.open(parsed.url, "_blank");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (
      !confirmDeleteDoc ||
      confirmDeleteDoc.documentSource !== "session_supplementary"
    ) {
      return;
    }

    const docId = confirmDeleteDoc.id;
    setDeletingDocumentId(docId);

    try {
      await fetchFastApi({
        method: "DELETE",
        path: `${sessionBase}/supplementary-document/${docId}`,
      });

      setDocuments((prev) => prev.filter((doc) => doc.id !== docId));

      toast.success("Document deleted successfully");
      onDocumentDeleted?.();
      setConfirmDeleteDoc(null);
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Supplementary Documents</DialogTitle>
            <DialogDescription>
              View and manage this session&apos;s documents and its
              supplementary uploads
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center p-8">
              <LoadingSpinner />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              No supplementary documents found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => {
                  const fileName = doc.fileName;
                  const canDelete =
                    doc.documentSource === "session_supplementary";
                  const docId = doc.id;
                  const createdAt = doc.createdAt;

                  const getDocumentSourceLabel = () => {
                    switch (doc.documentSource) {
                      case "current_control":
                        return "Current Control";
                      case "parent_control":
                        return "Parent Control";
                      case "work_step":
                        return "Work Step";
                      case "session_supplementary":
                        return "Session Upload";
                    }
                  };

                  const getDocumentSourceVariant = ():
                    | "default"
                    | "outline"
                    | "secondary" => {
                    switch (doc.documentSource) {
                      case "current_control":
                        return "default";
                      case "parent_control":
                        return "outline";
                      case "work_step":
                        return "default";
                      case "session_supplementary":
                        return "secondary";
                    }
                  };

                  return (
                    <TableRow key={docId}>
                      <TableCell className="font-medium">{fileName}</TableCell>
                      <TableCell>
                        <Badge variant={getDocumentSourceVariant()}>
                          {getDocumentSourceLabel()}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(doc)}
                            loading={downloadingDocumentId === docId}
                            disabled={downloadingDocumentId === docId}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmDeleteDoc(doc)}
                              disabled={deletingDocumentId === docId}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDoc !== null}
        onOpenChange={(open) => !open && setConfirmDeleteDoc(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{confirmDeleteDoc?.fileName}
              &quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteDoc(null)}
              disabled={deletingDocumentId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              loading={deletingDocumentId !== null}
              disabled={deletingDocumentId !== null}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
